import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { Chip } from "@mui/material";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import TableContainer from "../../../../../Components/Common/TableContainer";
import LoadingSpinner from "../../../../../Components/Common/Spinner";
import NoDataContent from "../../../../../Components/Common/NoDataContent";
import DeleteModal from "../../../../../Components/Common/DeleteModal";
import HandleDateTime from "../../../../../Components/Common/HandleDateTime";
import {
  deleteEmailInboundRule,
  deleteEmailInboundRuleSuccess,
  emailInboundRuleData,
  emailInboundRuleDataSuccess,
  emailInboundRuleItemFieldList,
  emailInboundRuleList,
  runEmailInboundRule,
  runEmailInboundRuleSuccess,
  systemFunctionOperandList,
} from "../../../../../store/CRMitEmailInbox/action";
import { RULE_ACTIONS } from "./constants";
import CreateEditEmailRule from "./CreateEditEmailRule";

export default function TabEmailRules() {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");

  const {
    rulesListAPI,
    rulesListLoading,
    fieldListAPI,
    operandListAPI,
    singleRuleAPI,
    deleteAPI,
    runAPI,
  } = useSelector((state) => ({
    rulesListAPI: state.EmailInbound.emailInboundRuleList?.emailinboundrule_list,
    rulesListLoading: state.EmailInbound.emailInboundRuleListLoading,
    fieldListAPI:
      state.EmailInbound.emailInboundRuleItemFieldList
        ?.emailinboundruleitemfield_list,
    operandListAPI:
      state.EmailInbound.systemFunctionOperandList?.systemfunctionoperand_list,
    singleRuleAPI: state.EmailInbound.emailInboundRuleData?.emailinboundrule_data,
    deleteAPI: state.EmailInbound.emailInboundRuleDelete,
    runAPI: state.EmailInbound.emailInboundRuleRun,
  }));

  useEffect(() => {
    dispatch(emailInboundRuleList());
    dispatch(emailInboundRuleItemFieldList());
    dispatch(systemFunctionOperandList());
  }, [dispatch]);

  // react-select option lists (numeric values to match payload types).
  const fieldOptions = useMemo(
    () =>
      (fieldListAPI || []).map((f) => ({
        value: Number(f.Id),
        label: f.Name,
        description: f.Description,
      })),
    [fieldListAPI]
  );
  const operandOptions = useMemo(
    () =>
      (operandListAPI || []).map((o) => ({
        value: Number(o.Id),
        label: o.Name,
        description: o.Description,
      })),
    [operandListAPI]
  );

  // Quick lookup maps for rendering the condition summary.
  const fieldName = useMemo(() => {
    const m = {};
    (fieldListAPI || []).forEach((f) => (m[String(f.Id)] = f.Name));
    return m;
  }, [fieldListAPI]);
  const operandName = useMemo(() => {
    const m = {};
    (operandListAPI || []).forEach((o) => (m[String(o.Id)] = o.Name));
    return m;
  }, [operandListAPI]);

  const searchResults = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return rulesListAPI || [];
    return (rulesListAPI || []).filter((r) =>
      [r.Title, r.Description].some((v) =>
        v?.toString().toLowerCase().includes(term)
      )
    );
  }, [search, rulesListAPI]);

  // Modal + delete state
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const toggle = () => setModal((v) => !v);

  const handleCreate = () => {
    setIsEdit(false);
    dispatch(emailInboundRuleDataSuccess([])); // clear stale single data
    setModal(true);
  };
  const handleEdit = (id) => {
    setIsEdit(true);
    dispatch(emailInboundRuleData({ id }));
    setModal(true);
  };

  const [deleteModal, setDeleteModal] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const handleDelete = () => {
    if (toDelete != null) dispatch(deleteEmailInboundRule([Number(toDelete)]));
  };

  useEffect(() => {
    if (isEmpty(deleteAPI)) return;
    if (deleteAPI?.status === 200) {
      toast.success(deleteAPI.message);
      dispatch(emailInboundRuleList());
      setDeleteModal(false);
      setToDelete(null);
    } else {
      toast.error(deleteAPI?.message || "Failed to delete rule");
    }
    dispatch(deleteEmailInboundRuleSuccess([]));
  }, [deleteAPI]);

  // Run a rule across all mailboxes now
  const [runningId, setRunningId] = useState(null);
  const handleRunRule = (id) => {
    setRunningId(id);
    dispatch(runEmailInboundRule({ id }));
  };

  useEffect(() => {
    if (isEmpty(runAPI)) return;
    if (runAPI?.status === 200) {
      toast.success(
        `${runAPI.message || "Rule executed successfully"} — ${
          runAPI.QtyEmailsArchived ?? 0
        } email(s) archived`
      );
      dispatch(emailInboundRuleList());
    } else {
      toast.error(runAPI?.message || "Failed to run rule");
    }
    setRunningId(null);
    dispatch(runEmailInboundRuleSuccess([]));
  }, [runAPI]);

  const refreshList = () => dispatch(emailInboundRuleList());

  const columns = useMemo(
    () => [
      {
        Header: "Id",
        accessor: "Id",
        filterable: false,
        Cell: ({ row }) => <span className="text-muted">#{row.original.Id}</span>,
      },
      {
        Header: "Rule",
        accessor: "Title",
        filterable: true,
        Cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{row.original.Title}</div>
            {row.original.Description && (
              <div className="text-muted fs-12">{row.original.Description}</div>
            )}
          </div>
        ),
      },
      {
        Header: "Conditions",
        Cell: ({ row }) => {
          const items = row.original.emailinboundruleitem_list || [];
          const joiner =
            Number(row.original.FlagMatchAll) === 1 ? " AND " : " OR ";
          if (isEmpty(items))
            return <span className="text-muted fs-12">No conditions</span>;
          return (
            <div className="fs-12">
              {items.map((it, i) => (
                <span key={it.Id || i}>
                  {i > 0 && <span className="text-muted">{joiner}</span>}
                  <span className="fw-medium">
                    {fieldName[String(it.EmailInboundRuleItemField_Id)] ||
                      `Field ${it.EmailInboundRuleItemField_Id}`}
                  </span>{" "}
                  <span className="text-muted">
                    {operandName[String(it.SystemFunctionOperand_Id)] ||
                      it.SystemFunctionOperand_Id}
                  </span>{" "}
                  <span className="badge bg-light text-dark">{it.Value}</span>
                </span>
              ))}
            </div>
          );
        },
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-1 flex-wrap">
            {RULE_ACTIONS.filter(
              (a) => Number(row.original[a.key]) === 1
            ).map((a) => (
              <Chip
                key={a.key}
                size="small"
                color={a.colour || "default"}
                label={a.label}
              />
            ))}
          </div>
        ),
      },
      {
        Header: "Active",
        accessor: "FlagActive",
        Cell: ({ row }) =>
          Number(row.original.FlagActive) === 1 ? (
            <Chip size="small" color="success" label="Active" />
          ) : (
            <Chip size="small" label="Inactive" />
          ),
      },
      {
        Header: "Created",
        accessor: "CreatedDateTime",
        Cell: ({ row }) => (
          <span className="text-muted fs-12">
            {HandleDateTime(row.original.CreatedDateTime)}
          </span>
        ),
      },
      {
        Header: "Action",
        Cell: ({ row }) => (
          <UncontrolledDropdown>
            <DropdownToggle
              className="btn btn-soft-info btn-icon fs-14"
              type="button"
            >
              <i className="fa-solid fa-ellipsis" />
            </DropdownToggle>
            <DropdownMenu>
              <li>
                <DropdownItem
                  disabled={runningId === row.original.Id}
                  onClick={() => handleRunRule(row.original.Id)}
                >
                  <i
                    className={`align-bottom me-2 text-muted ${
                      runningId === row.original.Id
                        ? "fa-solid fa-spinner fa-spin"
                        : "ri-play-circle-fill"
                    }`}
                  />
                  {runningId === row.original.Id
                    ? "Running…"
                    : "Run on all mailboxes now"}
                </DropdownItem>
              </li>
              <li>
                <DropdownItem onClick={() => handleEdit(row.original.Id)}>
                  <i className="ri-pencil-fill align-bottom me-2 text-muted" />
                  Edit
                </DropdownItem>
              </li>
              <li>
                <DropdownItem
                  onClick={() => {
                    setToDelete(row.original.Id);
                    setDeleteModal(true);
                  }}
                >
                  <i className="ri-delete-bin-fill align-bottom me-2 text-muted" />
                  Delete
                </DropdownItem>
              </li>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
    ],
    [fieldName, operandName, runningId]
  );

  return (
    <>
      {deleteModal && (
        <DeleteModal
          show={deleteModal}
          onCloseClick={() => setDeleteModal(false)}
          onDeleteClick={handleDelete}
          title="Delete Email Rule"
          description="Are you sure you want to delete this email rule?"
        />
      )}

      {modal && (
        <CreateEditEmailRule
          modal={modal}
          setModal={setModal}
          toggle={toggle}
          isEdit={isEdit}
          updateData={isEdit ? singleRuleAPI : null}
          fieldOptions={fieldOptions}
          operandOptions={operandOptions}
          onSaved={refreshList}
        />
      )}

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader className="border-0">
              <Row className="g-4 align-items-center">
                <Col sm={4}>
                  <div className="search-box">
                    <Input
                      type="search"
                      className="form-control search"
                      placeholder="Search rules..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <i className="ri-search-line search-icon" />
                  </div>
                </Col>
                <div className="col-sm-auto ms-auto">
                  <button className="btn btn-success" onClick={handleCreate}>
                    <i className="ri-add-line align-bottom me-1" />
                    Create Rule
                  </button>
                </div>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              {rulesListLoading ? (
                <LoadingSpinner />
              ) : !isEmpty(searchResults) ? (
                <TableContainer
                  columns={columns}
                  data={searchResults}
                  isGlobalFilter={false}
                  isAddUserList={false}
                  customPageSize={10}
                  className="custom-header-css"
                  divClass="table-responsive table-card mb-0"
                  tableClass="align-middle table-nowrap"
                  theadClass="text-muted"
                />
              ) : !isEmpty(rulesListAPI) ? (
                <NoDataContent
                  titleText="No rules found"
                  paragraphText="Try another search keyword."
                />
              ) : (
                <NoDataContent
                  titleText="No email rules yet"
                  paragraphText="Create a rule to automatically act on incoming emails, e.g. archive newsletters."
                  useButton={true}
                  buttonText="Create Rule"
                  buttonOnClick={handleCreate}
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
}
